import { Component, Inject, OnInit, DOCUMENT, ChangeDetectionStrategy } from '@angular/core';
import { AuthService, User } from '@auth0/auth0-angular';
import { TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { LanguageService } from 'src/app/services/language.service';
import { checkIfAuth0User } from 'src/app/utils/checkIfAuth0User';
import { PasswordResetService } from 'src/app/services/password-reset.service';

export type UserProfile = {
  salutation?: string;
  firstName?: string;
  lastName?: string;
  locale?: string;
};

/**
 * Landing page on '/' (CIAM-418): shows the Benutzerkonto read-only and opens the Auth0
 * Form that changes it. The Form is rendered by the open-profile-form Action, so a change
 * starts a new login with the /authorize parameter profile_form; the existing Auth0
 * session identifies the user, no login page is shown. "Passwort ändern" mails Auth0's
 * reset link instead (CIAM-420).
 */
@Component({
    selector: 'app-account',
    templateUrl: './account.component.html',
    styleUrls: ['./account.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AccountComponent implements OnInit {
  user: User | null | undefined = null;
  profile: UserProfile | undefined;
  isAuth0User = true;

  constructor(
    public auth: AuthService,
    public translate: TranslateService,
    private languageService: LanguageService,
    private router: Router,
    private passwordReset: PasswordResetService,
    @Inject(DOCUMENT) private doc: Document
  ) {}

  ngOnInit() {
    window.scrollTo(0, 0);
    this.auth.user$.subscribe((user) => {
      this.user = user;
      if (!user) {
        return;
      }
      this.isAuth0User = checkIfAuth0User(user.sub);
      this.profile = user['com.fenaco.id/profile'];
      // Values are shown as translated text, so they follow the switch without a redraw.
      if (this.profile) {
        this.languageService.setLanguage(this.profile.locale);
      }
    });
  }

  editAccount() {
    this.openForm('account');
  }

  /** Auth0 mails its reset link (CIAM-420); see PasswordResetService. */
  changePassword() {
    this.passwordReset.sendResetLink(this.user?.email);
  }

  private openForm(profileForm: 'account') {
    this.auth.loginWithRedirect({
      // Read by AppComponent on the return, for the success message.
      appState: { profileForm },
      authorizationParams: {
        profile_form: profileForm,
        ui_locales: this.translate.currentLang,
      },
    });
  }

  logout() {
    this.auth.logout({ logoutParams: { returnTo: this.doc.location.origin } });
  }

  policy() {
    this.router.navigate(['/privacy']);
  }
}
